import type { CartItem, WooOrder, WooStoreConfig } from "../types";

export interface CartStoreGroup {
  store: WooStoreConfig;
  items: CartItem[];
  subtotal: number;
  itemCount: number;
}

export interface OrderStoreGroup {
  store: WooStoreConfig;
  orders: WooOrder[];
}

function storeSortRank(store: WooStoreConfig): number {
  return store.active === false ? 1 : 0;
}

function compareStores(a: WooStoreConfig, b: WooStoreConfig): number {
  const rankDiff = storeSortRank(a) - storeSortRank(b);
  if (rankDiff !== 0) return rankDiff;
  return a.name.localeCompare(b.name, "pt-BR");
}

function sortOrdersByDateDesc(orders: WooOrder[]): WooOrder[] {
  return [...orders].sort(
    (a, b) =>
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
  );
}

function fallbackStore(storeId: string, storeName?: string): WooStoreConfig {
  return { id: storeId, name: storeName ?? storeId };
}

export function groupCartItemsByStore(
  carts: Record<string, CartItem[]>,
  stores: WooStoreConfig[],
): CartStoreGroup[] {
  const storeById = new Map(stores.map((store) => [store.id, store]));

  return Object.entries(carts)
    .filter(([, items]) => (items?.length ?? 0) > 0)
    .map(([storeId, items]) => {
      const store = storeById.get(storeId);
      if (!store) return null;

      const subtotal = items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

      return { store, items, subtotal, itemCount };
    })
    .filter((group): group is CartStoreGroup => group !== null)
    .sort((a, b) => compareStores(a.store, b.store));
}

export function groupOrdersByStore(
  orders: WooOrder[],
  stores: WooStoreConfig[],
): OrderStoreGroup[] {
  const storeById = new Map(stores.map((store) => [store.id, store]));
  const grouped = new Map<string, WooOrder[]>();

  for (const order of orders) {
    const bucket = grouped.get(order.storeId) ?? [];
    bucket.push(order);
    grouped.set(order.storeId, bucket);
  }

  return Array.from(grouped.entries())
    .map(([storeId, storeOrders]) => {
      const store =
        storeById.get(storeId) ??
        fallbackStore(storeId, storeOrders[0]?.storeName);
      return { store, orders: sortOrdersByDateDesc(storeOrders) };
    })
    .sort((a, b) => compareStores(a.store, b.store));
}

export function countStoresWithOrders(orders: WooOrder[]): number {
  return new Set(orders.map((order) => order.storeId)).size;
}
